import React, { useEffect, useMemo, useRef, useState } from "react";
import PlayOutlineIcon from "@rsuite/icons/PlayOutline";
import PauseOutlineIcon from "@rsuite/icons/PauseOutline";
import CloseIcon from "@rsuite/icons/Close";
import { ArrowLeftLine, ArrowRightLine, Speaker } from "@rsuite/icons";
import { useTranslation } from "react-i18next";
import { isValidURL } from "../../../utils/helpers";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { useMediaContext } from "../../../context/MediaContext";
import { Modal } from "rsuite";
import "./MediaPlay.css";
interface MediaPlayProps {
  playUrl?: string;
  imageUrl?: string;
  mediaInfo?: any;
  uniqueId?: string;
}

const MediaPlay: React.FC<MediaPlayProps> = ({
  playUrl = null,
  imageUrl = null,
  mediaInfo = null,
  uniqueId = "id",
}) => {
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const mediaUniqeId = uniqueId;
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const modalAudioRef = useRef<HTMLAudioElement | null>(null);
  const [modalPlaying, setModalPlaying] = useState<boolean>(false);
  const [modalCurrentTime, setModalCurrentTime] = useState<number>(0);
  const [modalDuration, setModalDuration] = useState<number>(0);
  const [playError, setPlayError] = useState<boolean>(false);

  const mediaUrl = playUrl || mediaInfo?.media_url || mediaInfo?.media_file_url;
  const mediaImageUrl =
    imageUrl || mediaInfo?.media_image || mediaInfo?.media_image_url;
  const mediaType = useMemo(() => {
    const apiMediaType = mediaInfo?.media_type?.toLowerCase?.();
    const url = mediaUrl?.toLowerCase?.() || "";

    if (apiMediaType === "video" || url.includes(".mp4")) {
      return "video";
    }

    return "audio";
  }, [mediaInfo?.media_type, mediaUrl]);

  const normalizedMediaInfo = useMemo(
    () => ({
      ...mediaInfo,
      media_url: mediaUrl,
      media_image_url: mediaImageUrl,
      media_image: mediaImageUrl,
      media_type: mediaType,
    }),
    [mediaInfo, mediaUrl, mediaImageUrl, mediaType]
  );

  const artworkUrl = isValidURL(mediaImageUrl)
    ? mediaImageUrl
    : loggedInUserDetail.song_default_image;
  const canPlayMedia = isValidURL(mediaUrl);

  const singerNames = useMemo(() => {
    return [
      mediaInfo?.singer1_name,
      mediaInfo?.singer2_name,
      mediaInfo?.singer3_name,
      mediaInfo?.singer4_name,
    ]
      .filter(Boolean)
      .join(", ");
  }, [
    mediaInfo?.singer1_name,
    mediaInfo?.singer2_name,
    mediaInfo?.singer3_name,
    mediaInfo?.singer4_name,
  ]);

  const infoChips = [
    mediaInfo?.album_name,
    mediaInfo?.language_title,
    mediaInfo?.license_title,
  ].filter(Boolean);

  useEffect(() => {
    const audio = modalAudioRef.current;

    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () => {
      setModalDuration(audio.duration || 0);
      setPlayError(false);
    };

    const handleTimeUpdate = () => {
      setModalCurrentTime(audio.currentTime || 0);
    };

    const handleEnded = () => {
      setModalPlaying(false);
    };

    const handleError = () => {
      setPlayError(true);
      setModalPlaying(false);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [open, mediaUrl]);

  useEffect(() => {
    const audio = modalAudioRef.current;

    if (!audio) {
      return;
    }

    if (open && canPlayMedia) {
      audio.currentTime = 0;
      setModalCurrentTime(0);
      setPlayError(false);
      audio.play().then(() => {
        setModalPlaying(true);
      }).catch(() => {
        setModalPlaying(false);
      });
    }

    if (!open) {
      audio.pause();
      audio.currentTime = 0;
      setModalPlaying(false);
      setModalCurrentTime(0);
    }
  }, [open, canPlayMedia, mediaUrl]);

  const formatTime = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds || 0));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const toggleModalPlayback = async () => {
    const audio = modalAudioRef.current;

    if (!audio || !canPlayMedia) {
      return;
    }

    if (modalPlaying) {
      audio.pause();
      setModalPlaying(false);
      return;
    }

    try {
      await audio.play();
      setModalPlaying(true);
    } catch (error) {
      setModalPlaying(false);
    }
  };

  const seekAudioBy = (seconds: number) => {
    const audio = modalAudioRef.current;

    if (!audio) {
      return;
    }

    audio.currentTime = Math.min(
      Math.max(audio.currentTime + seconds, 0),
      modalDuration || audio.duration || 0
    );
    setModalCurrentTime(audio.currentTime);
  };

  const handleProgressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = modalAudioRef.current;
    const newTime = Number(event.target.value);

    if (!audio) {
      return;
    }

    audio.currentTime = newTime;
    setModalCurrentTime(newTime);
  };

  const openPlayerModal = () => {
    if (canPlayMedia) {
      setIsPlaying(true);
      setPlayingMediaInfo(normalizedMediaInfo);
      return;
    }

    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  return (
    <div className="media-play-card">
      <Modal
        open={open}
        onClose={handleCloseModal}
        size={"xs"}
        className="media-player-modal"
      >
        <Modal.Body>
          <div className="media-player-shell">
            <button
              className="media-player-close"
              type="button"
              onClick={handleCloseModal}
              aria-label={t("CLOSE")}
            >
              <CloseIcon />
            </button>
            <audio ref={modalAudioRef} src={canPlayMedia ? mediaUrl : ""} />
            <div className="media-player-artwork-wrap">
              <div className="media-player-halo" />
              <img src={artworkUrl} alt={mediaInfo?.title || "Song artwork"} />
            </div>
            <div className="media-player-copy">
              <div className="media-player-eyebrow">Now Playing</div>
              <div className="media-player-title">
                {mediaInfo?.title || mediaInfo?.name || "Untitled Track"}
              </div>
              <div className="media-player-meta">
                {singerNames ||
                  mediaInfo?.composer ||
                  mediaInfo?.album_name ||
                  "Song preview"}
              </div>
              {infoChips.length > 0 && (
                <div className="media-player-chip-row">
                  {infoChips.map((chip) => (
                    <span className="media-player-chip" key={chip}>
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="media-player-progress-block">
              <input
                className="media-player-progress"
                type="range"
                min={0}
                max={modalDuration || 0}
                value={Math.min(modalCurrentTime, modalDuration || 0)}
                onChange={handleProgressChange}
                disabled={!canPlayMedia}
              />
              <div className="media-player-time-row">
                <span>{formatTime(modalCurrentTime)}</span>
                <span>{formatTime(modalDuration)}</span>
              </div>
            </div>
            <div className="media-player-controls">
              <button
                className="media-player-control media-player-secondary"
                type="button"
                onClick={() => seekAudioBy(-10)}
                disabled={!canPlayMedia}
              >
                <ArrowLeftLine />
                <span>10s</span>
              </button>
              <button
                className="media-player-control media-player-primary"
                type="button"
                onClick={toggleModalPlayback}
                disabled={!canPlayMedia}
              >
                {modalPlaying ? <PauseOutlineIcon /> : <PlayOutlineIcon />}
              </button>
              <button
                className="media-player-control media-player-secondary"
                type="button"
                onClick={() => seekAudioBy(10)}
                disabled={!canPlayMedia}
              >
                <span>10s</span>
                <ArrowRightLine />
              </button>
            </div>
            <div className="media-player-status">
              {playError
                ? "This media preview is not playable."
                : canPlayMedia
                ? `Preview ${mediaType} ready`
                : "Media preview unavailable"}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <button
        type="button"
        className="media-image-trigger"
        onClick={openPlayerModal}
        title={mediaType === "video" ? "Video file" : "Audio file"}
      >
        <img className="round-image media-cover-image" src={artworkUrl} />
        <span
          className={`media-type-badge media-type-badge-${mediaType}`}
          aria-label={mediaType === "video" ? "Video file" : "Audio file"}
        >
          {mediaType === "video" ? <PlayOutlineIcon /> : <Speaker />}
        </span>
        <span className="media-image-overlay">
          <PlayOutlineIcon />
        </span>
      </button>

      <div
        className={`${
          isPlaying &&
          playingMediaInfo[mediaUniqeId] === normalizedMediaInfo[mediaUniqeId]
            ? "playing"
            : ""
        } file-preview`}
        onClick={() => {
          setIsPlaying(true);
          setPlayingMediaInfo(normalizedMediaInfo);
        }}
      >
        {isPlaying &&
        playingMediaInfo[mediaUniqeId] === normalizedMediaInfo[mediaUniqeId] ? (
          <PauseOutlineIcon />
        ) : (
          <PlayOutlineIcon />
        )}
      </div>
    </div>
  );
};

export default MediaPlay;
